import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Paper, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Checkbox, FormControlLabel, Chip, Snackbar, Alert,
    CircularProgress, Tooltip, Switch,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { GetRolesWithModules, CreateRole, UpdateRoleModules, DeleteRole, GetAvailableModules } from '../../Redux/UsersDucks';

// Fallback labels for modules that may not have a Label from the backend yet
const MODULE_LABEL_FALLBACK = {
    flow_messages: 'Flujo de Mensajes',
    messaging: 'Vista de Mensajería',
    multi_conv: 'Smart Talking',
    smart_talking: 'Smart Talking',
    campaigns: 'Campañas',
    admin: 'Panel de Administración',
    integracion: 'Integraciones',
    stats: 'Estadísticas',
};

const getModuleLabel = (module) =>
    module.label || MODULE_LABEL_FALLBACK[module.name] || module.name;

const RolePage = () => {
    const dispatch = useDispatch();
    const rolesWithModules = useSelector(store => store.users.listRolWithModules);
    const availableModules = useSelector(store => store.users.availableModules);

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleName, setRoleName] = useState('');
    const [roleDescription, setRoleDescription] = useState('');
    const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        dispatch(GetRolesWithModules());
        dispatch(GetAvailableModules());
    }, [dispatch]);

    const openCreateModal = () => {
        setSelectedRole(null);
        setRoleName('');
        setRoleDescription('');
        setSelectedPermissionIds([]);
        setModalOpen(true);
    };

    const openEditModal = (role) => {
        setSelectedRole(role);
        setRoleName(role.name || '');
        setRoleDescription(role.description || '');
        setSelectedPermissionIds(role.modules?.map(m => m.id) || []);
        setModalOpen(true);
    };

    const handleTogglePermission = (permId) => {
        setSelectedPermissionIds(prev =>
            prev.includes(permId) ? prev.filter(id => id !== permId) : [...prev, permId]
        );
    };

    const handleChildToggle = (childId, parentModule, childModules) => {
        setSelectedPermissionIds(prev => {
            const next = prev.includes(childId)
                ? prev.filter(id => id !== childId)
                : [...prev, childId];
            const anyChildActive = childModules.some(m => next.includes(m.id));
            if (anyChildActive && !next.includes(parentModule.id)) return [...next, parentModule.id];
            if (!anyChildActive) return next.filter(id => id !== parentModule.id);
            return next;
        });
    };

    const handleSave = async () => {
        if (!roleName.trim()) return;
        setSaving(true);
        try {
            const payload = { name: roleName, description: roleDescription, permissionIds: selectedPermissionIds };
            if (selectedRole) {
                await dispatch(UpdateRoleModules(selectedRole.id, payload));
                showSnackbar('Rol actualizado correctamente');
            } else {
                await dispatch(CreateRole(payload));
                showSnackbar('Rol creado correctamente');
            }
            setModalOpen(false);
        } catch {
            showSnackbar('Error al guardar el rol', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedRole) return;
        try {
            await dispatch(DeleteRole(selectedRole.id));
            showSnackbar('Rol eliminado correctamente');
            setDeleteDialogOpen(false);
            setSelectedRole(null);
        } catch {
            showSnackbar('Error al eliminar el rol', 'error');
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Gestión de Roles</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}
                    sx={{ bgcolor: '#007AFF', '&:hover': { bgcolor: '#005ecb' } }}>
                    Nuevo Rol
                </Button>
            </Box>

            <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                {/* Header */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 100px', bgcolor: '#F2F2F7', px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>Nombre</Typography>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>Módulos</Typography>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>Acciones</Typography>
                </Box>

                {rolesWithModules.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">No hay roles configurados</Typography>
                    </Box>
                ) : (
                    rolesWithModules.map(role => (
                        <Box key={role.id} sx={{
                            display: 'grid', gridTemplateColumns: '1fr 2fr 100px',
                            px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0',
                            '&:last-child': { borderBottom: 'none' },
                            '&:hover': { bgcolor: '#fafafa' },
                        }}>
                            <Box>
                                <Typography variant="body2" fontWeight={600}>{role.name}</Typography>
                                {role.description && (
                                    <Typography variant="caption" color="text.secondary">{role.description}</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                {(role.modules || []).length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">Sin módulos</Typography>
                                ) : (
                                    role.modules.map(m => (
                                        <Chip
                                            key={m.id}
                                            label={getModuleLabel(m)}
                                            size="small"
                                            sx={{ fontSize: 11, bgcolor: '#e8f0fe', color: '#1a56db', fontWeight: 500 }}
                                        />
                                    ))
                                )}
                            </Box>
                            <Box>
                                <Tooltip title="Editar">
                                    <IconButton size="small" onClick={() => openEditModal(role)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                    <IconButton size="small" onClick={() => { setSelectedRole(role); setDeleteDialogOpen(true); }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))
                )}
            </Paper>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{selectedRole ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre del rol"
                        fullWidth
                        value={roleName}
                        onChange={e => setRoleName(e.target.value)}
                        margin="normal"
                        required
                    />
                    <TextField
                        label="Descripción"
                        fullWidth
                        value={roleDescription}
                        onChange={e => setRoleDescription(e.target.value)}
                        margin="normal"
                    />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1.5 }}>
                        Permisos por módulo
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {availableModules
                            .filter(m => !m.parentId)
                            .map(parentModule => {
                                const childModules = availableModules
                                    .filter(m => m.parentId === parentModule.id)
                                    .sort((a, b) => a.displayOrder - b.displayOrder);

                                const checkedChildren = childModules.filter(m => selectedPermissionIds.includes(m.id));
                                const allChecked = childModules.length > 0 && checkedChildren.length === childModules.length;
                                const someChecked = checkedChildren.length > 0 && !allChecked;
                                const parentChecked = selectedPermissionIds.includes(parentModule.id);
                                const isEnabled = childModules.length > 0 ? (allChecked || someChecked) : parentChecked;

                                const statusLabel = !isEnabled ? 'Sin acceso' : someChecked ? 'Acceso parcial' : 'Acceso completo';
                                const borderColor = !isEnabled ? '#e0e0e0' : someChecked ? '#f59e0b' : '#22c55e';
                                const headerBg = !isEnabled ? '#fafafa' : someChecked ? '#fffbeb' : '#f0fdf4';
                                const statusSx = !isEnabled
                                    ? { bgcolor: '#f0f0f0', color: '#888', border: 'none' }
                                    : someChecked
                                        ? { bgcolor: '#fef3c7', color: '#b45309', border: '1px solid #f59e0b' }
                                        : { bgcolor: '#dcfce7', color: '#15803d', border: '1px solid #22c55e' };

                                const handleParentToggle = () => {
                                    if (allChecked || someChecked) {
                                        const idsToRemove = [parentModule.id, ...childModules.map(m => m.id)];
                                        setSelectedPermissionIds(prev => prev.filter(id => !idsToRemove.includes(id)));
                                    } else {
                                        const idsToAdd = [parentModule.id, ...childModules.map(m => m.id)];
                                        setSelectedPermissionIds(prev => [...new Set([...prev, ...idsToAdd])]);
                                    }
                                };

                                return (
                                    <Paper key={parentModule.id} elevation={0} sx={{ border: '1px solid', borderColor, borderRadius: 2, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                                        {/* Card header */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, bgcolor: headerBg, transition: 'background-color 0.2s' }}>
                                            <Switch
                                                size="small"
                                                checked={isEnabled}
                                                onChange={childModules.length === 0
                                                    ? () => handleTogglePermission(parentModule.id)
                                                    : handleParentToggle}
                                                sx={{
                                                    mr: 1,
                                                    '& .MuiSwitch-switchBase.Mui-checked': { color: someChecked ? '#f59e0b' : '#22c55e' },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: someChecked ? '#f59e0b' : '#22c55e' },
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" fontWeight={600}>{getModuleLabel(parentModule)}</Typography>
                                                {parentModule.description && (
                                                    <Typography variant="caption" color="text.secondary">{parentModule.description}</Typography>
                                                )}
                                            </Box>
                                            <Chip label={statusLabel} size="small" sx={{ fontSize: 11, fontWeight: 500, ...statusSx }} />
                                        </Box>

                                        {/* Sub-modules */}
                                        {childModules.length > 0 && (
                                            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: isEnabled ? borderColor : '#f0f0f0', bgcolor: '#fff' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        Sub-módulos
                                                    </Typography>
                                                    {isEnabled && (
                                                        <Button
                                                            size="small"
                                                            sx={{ fontSize: 11, py: 0, minWidth: 0, color: allChecked ? '#d32f2f' : '#007AFF' }}
                                                            onClick={handleParentToggle}
                                                        >
                                                            {allChecked ? 'Quitar todos' : 'Seleccionar todos'}
                                                        </Button>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                                                    {childModules.map(child => (
                                                        <FormControlLabel
                                                            key={child.id}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={selectedPermissionIds.includes(child.id)}
                                                                    onChange={() => handleChildToggle(child.id, parentModule, childModules)}
                                                                    disabled={!isEnabled}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2" color={!isEnabled ? 'text.disabled' : 'text.primary'}>
                                                                    {getModuleLabel(child)}
                                                                </Typography>
                                                            }
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !roleName.trim()}
                        sx={{ bgcolor: '#007AFF', '&:hover': { bgcolor: '#005ecb' } }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <Typography>¿Deseas eliminar el rol <strong>{selectedRole?.name}</strong>?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RolePage;
